import { formatEuro, formatDateDE } from './finanzen-tab';

export interface FahrzeugDetailFinanzdaten {
  kaufpreis_netto: number | null;
  kaufpreis_brutto: number | null;
  bruttolistenpreis: number | null;
  finanzierungspartner: string | null;
  finanzierungsart: string | null;
  monatliche_rate: number | null;
  restschuld: number | null;
  laufzeitende: string | null;
  anschaffungsdatum: string | null;
  afa_beginn: string | null;
  afa_nutzungsdauer_jahre: number | null;
  restbuchwert: number | null;
  versicherung_gesellschaft: string | null;
  versicherung_police_nr: string | null;
  versicherung_jahreswechsel: string | null;
  buchhaltungskonto: string | null;
  darlehensnummer: string | null;
}

export interface FinanzZeile {
  label: string;
  value: string;
}

export interface FinanzSektion {
  titel: string;
  zeilen: FinanzZeile[];
}

function hasText(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

function hasNumber(v: number | null | undefined): v is number {
  return typeof v === 'number' && !Number.isNaN(v);
}

export function buildFinanzSektionen(fz: FahrzeugDetailFinanzdaten): FinanzSektion[] {
  const sektionen: FinanzSektion[] = [];

  const finanzierung: FinanzZeile[] = [];
  if (hasNumber(fz.kaufpreis_netto)) finanzierung.push({ label: 'Kaufpreis netto', value: formatEuro(fz.kaufpreis_netto) });
  if (hasNumber(fz.kaufpreis_brutto)) finanzierung.push({ label: 'Kaufpreis brutto', value: formatEuro(fz.kaufpreis_brutto) });
  if (hasNumber(fz.bruttolistenpreis)) finanzierung.push({ label: 'Bruttolistenpreis', value: formatEuro(fz.bruttolistenpreis) });
  if (hasText(fz.anschaffungsdatum)) finanzierung.push({ label: 'Anschaffungsdatum', value: formatDateDE(fz.anschaffungsdatum) });
  if (hasText(fz.finanzierungspartner)) finanzierung.push({ label: 'Finanzierungspartner', value: fz.finanzierungspartner.trim() });
  if (hasText(fz.finanzierungsart)) finanzierung.push({ label: 'Finanzierungsart', value: fz.finanzierungsart.trim() });
  if (hasNumber(fz.monatliche_rate)) finanzierung.push({ label: 'Monatliche Rate', value: formatEuro(fz.monatliche_rate) });
  if (hasNumber(fz.restschuld)) finanzierung.push({ label: 'Restschuld', value: formatEuro(fz.restschuld) });
  if (hasText(fz.laufzeitende)) finanzierung.push({ label: 'Laufzeitende', value: formatDateDE(fz.laufzeitende) });
  if (finanzierung.length > 0) sektionen.push({ titel: 'Finanzierung', zeilen: finanzierung });

  const afa: FinanzZeile[] = [];
  if (hasText(fz.afa_beginn)) afa.push({ label: 'AfA-Beginn', value: formatDateDE(fz.afa_beginn) });
  if (hasNumber(fz.afa_nutzungsdauer_jahre)) {
    afa.push({ label: 'Nutzungsdauer', value: fz.afa_nutzungsdauer_jahre === 1 ? '1 Jahr' : `${fz.afa_nutzungsdauer_jahre} Jahre` });
  }
  if (hasNumber(fz.restbuchwert)) afa.push({ label: 'Restbuchwert', value: formatEuro(fz.restbuchwert) });
  if (afa.length > 0) sektionen.push({ titel: 'AfA / Buchwert', zeilen: afa });

  const versicherung: FinanzZeile[] = [];
  if (hasText(fz.versicherung_gesellschaft)) versicherung.push({ label: 'Gesellschaft', value: fz.versicherung_gesellschaft.trim() });
  if (hasText(fz.versicherung_police_nr)) versicherung.push({ label: 'Police-Nr.', value: fz.versicherung_police_nr.trim() });
  if (hasText(fz.versicherung_jahreswechsel)) versicherung.push({ label: 'Jahreswechsel', value: formatDateDE(fz.versicherung_jahreswechsel) });
  if (versicherung.length > 0) sektionen.push({ titel: 'Versicherung', zeilen: versicherung });

  const buchhaltung: FinanzZeile[] = [];
  if (hasText(fz.buchhaltungskonto)) buchhaltung.push({ label: 'Buchhaltungskonto', value: fz.buchhaltungskonto.trim() });
  if (hasText(fz.darlehensnummer)) buchhaltung.push({ label: 'Darlehensnummer', value: fz.darlehensnummer.trim() });
  if (buchhaltung.length > 0) sektionen.push({ titel: 'Buchhaltung', zeilen: buchhaltung });

  return sektionen;
}
